import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Bank, UploadSimple, CheckCircle, Copy, Receipt, CurrencyDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Section 8 — Student Payment Workflow.
 * Bank transfer ONLY. Student sees account details, completes transfer,
 * uploads proof and submits — system marks Pending Verification.
 */
export default function PayNowPage() {
  const { programmeId } = useParams();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState(null);
  const [prog, setProg] = useState(null);
  const [currency, setCurrency] = useState("NGN");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // Section 1 — discount / scholarship declaration
  const [discountClaimed, setDiscountClaimed] = useState(false);
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountNote, setDiscountNote] = useState("");
  // Live exchange rate so the student sees the correct outstanding balance
  // regardless of whether they're paying in NGN or USD.
  const [exchangeRate, setExchangeRate] = useState(1500);
  const [enrolment, setEnrolment] = useState(null);

  useEffect(() => {
    api.get("/payment/bank-accounts").then(r => setAccounts(r.data)).catch(() => {});
    api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn || 1500)).catch(() => {});
    if (programmeId) {
      api.get(`/programmes/${programmeId}`).then(r => setProg(r.data)).catch(() => {});
      api.get("/enrollments/my").then(r => {
        const match = (r.data || []).find(e => e.programme_id === programmeId);
        setEnrolment(match || null);
      }).catch(() => {});
    }
  }, [programmeId]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  // Compute live outstanding balance in the selected currency.
  const totalFee = parseFloat(enrolment?.total_fee || prog?.price_usd || prog?.fee || 0);
  const alreadyPaid = parseFloat(enrolment?.amount_paid || 0);
  const discountVal = discountClaimed ? parseFloat(discountAmount || 0) : 0;
  const payableUsd = Math.max(0, totalFee - discountVal);
  const balanceUsd = Math.max(0, payableUsd - alreadyPaid);
  const amountUsdEquivalent = currency === "NGN" && exchangeRate > 0
    ? (parseFloat(amount || 0) / exchangeRate)
    : parseFloat(amount || 0);
  const balanceAfterThisPayment = Math.max(0, balanceUsd - amountUsdEquivalent);
  const fmt = (usdVal) => currency === "NGN"
    ? `₦${Math.round(usdVal * exchangeRate).toLocaleString()}`
    : `$${usdVal.toFixed(2)}`;

  const submit = async () => {
    if (!file) return toast.error("Please upload your transfer receipt");
    if (!amount || parseFloat(amount) <= 0) return toast.error("Please enter the amount transferred");
    if (discountClaimed && (!discountAmount || parseFloat(discountAmount) <= 0)) {
      return toast.error("Please enter the discount amount you received");
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("programme_id", programmeId || "");
      fd.append("amount", amount);
      fd.append("currency", currency);
      fd.append("reference", reference);
      fd.append("note", note);
      fd.append("discount_claimed", discountClaimed ? String(parseFloat(discountAmount || 0)) : "0");
      fd.append("discount_note", discountClaimed ? discountNote : "");
      await api.post("/payment/transfer/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <Layout>
        <div className="p-6 md:p-10 max-w-2xl mx-auto" data-testid="pay-success">
          <div className="bg-white border border-emerald-200 p-8 text-center">
            <CheckCircle size={64} weight="fill" className="text-emerald-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Payment Pending Verification</h1>
            <p className="text-sm text-slate-600 mb-6">Thanks! Your transfer evidence has been received and forwarded to our payments team. You'll be notified the moment it's approved (usually within 24 hours on business days).</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Go to Dashboard</Button>
          </div>
        </div>
      </Layout>
    );
  }

  const acct = accounts ? accounts[currency] : null;

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="pay-now-page">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Payment</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Pay by bank transfer</h1>
        {prog && <p className="text-sm text-slate-600 mb-6">For <b>{prog.code || ""} · {prog.title}</b></p>}

        {/* Step 1 — Currency switch */}
        <div className="bg-white border border-slate-200 p-5 mb-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Step 1 · Select currency</p>
          <div className="flex gap-2">
            {["NGN", "USD"].map(c => (
              <button key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`} className={`flex-1 px-4 py-3 border-2 ${currency === c ? "border-blue-800 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-600 hover:border-blue-300"}`}>
                <p className="text-sm font-bold">{c === "NGN" ? "Pay in ₦ Naira" : "Pay in $ USD"}</p>
                <p className="text-xs text-slate-500">{c === "NGN" ? "Domestic transfer" : "International transfer"}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2 — Bank details */}
        {acct && (
          <div className="bg-slate-950 text-white p-6 mb-4" data-testid="bank-details-card">
            <div className="flex items-center gap-2 mb-4">
              <Bank size={20} weight="fill" className="text-amber-300" />
              <p className="text-xs uppercase tracking-widest text-amber-300 font-bold">Step 2 · Transfer to this account</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/60">Bank</p>
                <p className="text-base font-bold flex items-center gap-2">{acct.bank_name} <button onClick={() => copy(acct.bank_name, "Bank")} className="text-amber-300 hover:text-amber-100"><Copy size={14} /></button></p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/60">Account Name</p>
                <p className="text-base font-bold flex items-center gap-2">{acct.account_name} <button onClick={() => copy(acct.account_name, "Account name")} className="text-amber-300 hover:text-amber-100"><Copy size={14} /></button></p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase tracking-wider text-white/60">Account Number</p>
                <p className="text-2xl font-bold tracking-wider flex items-center gap-2" data-testid="account-number-display">{acct.account_number} <button onClick={() => copy(acct.account_number, "Account number")} className="text-amber-300 hover:text-amber-100"><Copy size={16} /></button></p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase tracking-wider text-white/60">Currency</p>
                <p className="text-base font-bold flex items-center gap-1">{currency === "NGN" ? "Nigerian Naira (₦)" : "US Dollars ($)"} <CurrencyDollar size={14} className="text-amber-300" /></p>
              </div>
            </div>
            <p className="text-xs text-amber-200 mt-4 leading-relaxed">📌 Please use your full name as the transfer reference so we can match the payment to your account.</p>
          </div>
        )}

        {/* Step 3 — Upload receipt */}
        <div className="bg-white border border-slate-200 p-5 mb-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Step 3 · Submit your proof of transfer</p>

          {/* Live outstanding-balance summary (in selected currency).
              Renders whenever we know the programme — even if total_fee is 0,
              we still show the live "after this payment" line so the student
              has an unambiguous summary card. */}
          <div className="bg-slate-50 border border-slate-200 p-3 mb-3 text-xs" data-testid="balance-summary">
            <div className="flex justify-between"><span className="text-slate-500">Programme fee</span><span className="font-semibold">{fmt(totalFee)}</span></div>
            {alreadyPaid > 0 && <div className="flex justify-between"><span className="text-slate-500">Already paid</span><span className="font-semibold text-emerald-700">−{fmt(alreadyPaid)}</span></div>}
            {discountVal > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount declared</span><span className="font-semibold text-blue-700">−{fmt(discountVal)}</span></div>}
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1"><span className="text-slate-500">Outstanding now</span><span className="font-bold" data-testid="outstanding-now">{fmt(balanceUsd)}</span></div>
            <div className="flex justify-between text-amber-700 mt-1"><span>After this payment</span><span className="font-bold" data-testid="outstanding-after">{fmt(balanceAfterThisPayment)}</span></div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount transferred ({currency})</Label>
                <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="rounded-none" data-testid="amount-input" />
              </div>
              <div>
                <Label>Transfer reference / number (optional)</Label>
                <Input value={reference} onChange={e => setReference(e.target.value)} className="rounded-none" data-testid="reference-input" />
              </div>
            </div>

            {/* Section 1 — Discount / scholarship declaration */}
            <div className="border border-blue-200 bg-blue-50/30 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={discountClaimed}
                  onChange={e => setDiscountClaimed(e.target.checked)}
                  className="mt-0.5"
                  data-testid="discount-claimed-toggle"
                />
                <div className="text-sm">
                  <p className="font-semibold text-blue-900">I received an approved discount / scholarship / waiver</p>
                  <p className="text-xs text-blue-800/80">Tick this if Admin has approved a reduced fee and the amount I'm paying represents my full obligation.</p>
                </div>
              </label>
              {discountClaimed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Discount amount ({currency})</Label>
                    <Input type="number" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)} className="rounded-none" data-testid="discount-amount-input" placeholder="e.g. 50000" />
                  </div>
                  <div>
                    <Label className="text-xs">Who approved it / details</Label>
                    <Input value={discountNote} onChange={e => setDiscountNote(e.target.value)} className="rounded-none" data-testid="discount-note-input" placeholder="e.g. Approved by Mr. A. Smith on…" />
                  </div>
                  <p className="text-[11px] text-blue-700 sm:col-span-2 italic">Your declaration is submitted for admin review. Admin may verify, edit, accept or reject the discount during reconciliation.</p>
                </div>
              )}
            </div>

            <div>
              <Label>Receipt / screenshot</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files?.[0])} className="rounded-none" data-testid="receipt-file-input" />
              <p className="text-xs text-slate-500 mt-1">Accepted: JPG, PNG, PDF, WEBP</p>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} className="rounded-none" rows={2} />
            </div>
            <Button onClick={submit} disabled={submitting} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none py-6 gap-2" data-testid="submit-payment-proof">
              {submitting ? "Submitting…" : <><UploadSimple size={16} weight="bold" /> Submit for Verification</>}
            </Button>
          </div>
        </div>

        <Link to="/payments/my" className="text-sm text-blue-800 hover:underline flex items-center gap-1" data-testid="view-my-transfers"><Receipt size={14} /> View my previous transfers</Link>
      </div>
    </Layout>
  );
}
